// src/components/resources/RelatedResources.tsx

import { type ReactNode } from 'react'
import type { ResourceInstance, ResourceTypeMap } from '~/lib/resources/types/shared/base'
import { ResourceType, GLOSSARY_CATEGORIES, getResourceTypeLabel } from '~/lib/resources/types/shared/enums'
import { resourceHref } from '~/lib/resources/resource-path'
import Link from 'next/link'
import Image from 'next/image'

const DEFAULT_RESOURCE_CENTER_OG_IMAGE = '/images/resources/adsights-digital-marketing-resource-center-home-hero-image.jpg'

interface RelatedResourcesProps {
  resources: ResourceInstance[]
  title?: string
  maxItems?: number
  /** Heading level for the section title — use h2 when nested under an h1 page title. */
  titleLevel?: 2 | 3
}

function isTool(resource: ResourceInstance): resource is ResourceTypeMap['tool'] {
  return resource.resourceType === ResourceType.TOOL
}

function isGlossaryTerm(resource: ResourceInstance): resource is ResourceTypeMap['glossary-term'] {
  return resource.resourceType === ResourceType.GLOSSARY_TERM
}

function getCategoryIcon(category: string | undefined): string {
  if (!category) return '📚' // Default icon
  const glossaryCategory = GLOSSARY_CATEGORIES.find(cat => cat.id === category)
  return glossaryCategory?.icon ?? '📚'
}

export function RelatedResources({ 
  resources, 
  title = "Related Resources",
  maxItems = 3,
  titleLevel = 3,
}: RelatedResourcesProps): ReactNode {
  if (!Array.isArray(resources) || resources.length === 0) return null

  const TitleTag = titleLevel === 2 ? 'h2' : 'h3'
  const titleClassName =
    titleLevel === 2
      ? 'text-2xl font-semibold text-gray-900 dark:text-white mb-6'
      : 'text-lg font-medium text-gray-900 dark:text-white mb-6'

  return (
    /* Entrance via the site's SSR-safe `data-reveal` mechanism (globals.css +
       RevealBootstrap) rather than a Motion `initial={{ opacity: 0 }}`
       entrance, which serialized `opacity:0` into the SSR HTML and hid these
       related-resource links until hydration for no-JS / blocked-JS visitors
       (same class as `r3543164053`, the layout-level wrapper). JS+motion
       visitors keep the fade-up entrance (now on scroll-into-view, which
       supersedes the old mount-time 0.2s stagger); server / no-JS /
       reduced-motion output is fully visible. */
    <div
      data-reveal
      style={{ '--reveal-translate': '0 20px' } as React.CSSProperties}
      className="mt-12"
    >
      <TitleTag className={titleClassName}>
        {title}
      </TitleTag>
      <div className="space-y-4">
        {resources.slice(0, maxItems).map(resource => (
          <Link
            key={resource.slug}
            href={resourceHref(resource)}
            className="group flex items-start gap-4 rounded-lg border border-gray-200 p-4 transition-colors hover:border-indigo-200 hover:bg-indigo-50 dark:border-gray-800 dark:hover:border-indigo-800/50 dark:hover:bg-indigo-900/20"
          >
            {isGlossaryTerm(resource) ? (
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-2xl dark:bg-indigo-900/20">
                {getCategoryIcon(resource.category)}
              </div>
            ) : (
              <div className="relative h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg">
                <Image
                  src={resource.images?.thumbnail?.url ?? DEFAULT_RESOURCE_CENTER_OG_IMAGE}
                  alt={resource.title}
                  fill
                  sizes="(max-width: 768px) 48px, (max-width: 1200px) 64px, 96px"
                  className="object-cover"
                />
              </div>
            )}
            <div>
              <h4 className="text-sm font-medium text-gray-900 group-hover:text-indigo-600 dark:text-white dark:group-hover:text-indigo-400">
                {resource.canonicalTitle ?? resource.title}
              </h4>
              <p className="mt-1 text-xs text-gray-600 line-clamp-2 dark:text-gray-400">
                {resource.description}
              </p>
              {/* Resource Type Badge */}
              <div className="mt-2">
                <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800 dark:bg-gray-800 dark:text-gray-200">
                  {getResourceTypeLabel(isTool(resource) ? resource.toolType : resource.resourceType)}
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  )
}